import { useEffect, useRef, useState } from "react";
import { MAX_PHOTO_BYTES, PHOTO_SIZE, type OperatorPhoto } from "../../shared/operator-photo";

type Api = <T>(path: string, init?: RequestInit) => Promise<T>;

async function thumbnail(file: File): Promise<Blob> {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) throw new Error("Choose a JPG, PNG or WebP photo.");
  if (file.size > 10 * 1024 * 1024) throw new Error("Choose a photo smaller than 10 MB.");
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = PHOTO_SIZE;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("This browser cannot prepare photos. Please try another browser.");
    const side = Math.min(bitmap.width, bitmap.height);
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, PHOTO_SIZE, PHOTO_SIZE);
    context.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, PHOTO_SIZE, PHOTO_SIZE);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85));
    if (!blob || blob.size > MAX_PHOTO_BYTES) throw new Error("Could not prepare this photo. Please choose a smaller image.");
    return blob;
  } finally { bitmap.close(); }
}

export function ProfilePhoto({ api, name }: { api: Api; name: string }) {
  const [photo, setPhoto] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  const [status, setStatus] = useState("");
  const [problem, setProblem] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let cancelled = false;
    void api<OperatorPhoto>("/api/admin/profile/photo").then((value) => { if (!cancelled) setPhoto(value.photoUrl); })
      .catch(() => { if (!cancelled) setProblem("Could not load your photo. Refresh to try again."); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [api]);

  async function save(file?: File) {
    setBusy(true); setProblem(""); setStatus("");
    try {
      const body = file ? await thumbnail(file) : undefined;
      const result = await api<OperatorPhoto>("/api/admin/profile/photo", { method: file ? "PUT" : "DELETE",
        ...(body ? { body, headers: { "Content-Type": "image/jpeg" } } : {}) });
      setPhoto(result.photoUrl);
      setStatus(file ? "Photo saved to your account." : "Photo removed.");
    } catch (error) { setProblem(error instanceof Error ? error.message : "Could not save your photo. Try again."); }
    finally { setBusy(false); if (fileInput.current) fileInput.current.value = ""; }
  }

  return <div className="profile-photo-settings">
    <div className="profile-photo-preview">{photo ? <img src={photo} alt={`Your customer-facing photo, ${name}`} /> : <span aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>}</div>
    <div>
      <strong>Your photo for customers</strong>
      <p>Shown alongside the other connected admins on the chat button and in the open chat—even before a customer starts chatting. Keep your live inbox connected to appear online.</p>
      <div className="profile-photo-actions">
        <input ref={fileInput} type="file" accept="image/jpeg,image/png,image/webp" aria-label="Choose your customer-facing photo" hidden disabled={busy} onChange={(event) => { const file = event.target.files?.[0]; if (file) void save(file); }} />
        <button type="button" className="button button-secondary" disabled={busy} onClick={() => fileInput.current?.click()}>{busy ? "Please wait…" : photo ? "Change photo" : "Upload photo"}</button>
        {photo && <button type="button" className="text-button" disabled={busy} onClick={() => void save()}>Remove photo</button>}
        <small>JPG, PNG or WebP · up to 10 MB · cropped to a square</small>
      </div>
      {status && <p role="status">{status}</p>}
      {problem && <p role="alert" className="chat-insights-error">{problem}</p>}
    </div>
  </div>;
}
